import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { AvailableAuthMethods, MFAMethods } from '../domain/AuthMethods';
import { useAuthAPI } from '../services/auth_api/AuthAPIHook';
import { authAPI } from '../services/auth_api/SessionAuthAPI';
import { SessionData } from '../domain/Session';
import { errorInvalidCredentials } from '../services/auth_api/Errors';
import SendCodeButton from '../components/SendCode';
import UserInfo from '../components/UserInfo';
import '../styles/Login.css';
import '../styles/RadioButton.css';
import '../styles/SendCode.css';

const otpSendInterval = 60; // Time interval in seconds for sending OTP codes

// Define the specific order you want
const methodOrder = [
    AvailableAuthMethods.TOTP,
    AvailableAuthMethods.OTP_EMAIL,
    AvailableAuthMethods.OTP_SMS,
];

const LoginMFA: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { sessionData, setSessionData } = useSession();
    const { submitOTP, submitOtpEmail, submitOtpSMS, sendOtpEmail, sendOtpSms } = useAuthAPI(authAPI);
    const [selectedMethod, setSelectedMethod] = useState<AvailableAuthMethods | null>(null);
    const [availableMethods, setAvailableMethods] = useState<AvailableAuthMethods[]>([]);
    const [code, setCode] = useState<string>(''); // State for the code entered by the user
    const [loading, setLoading] = useState<boolean>(false); // Loading state for button actions
    const [error, setError] = useState<string | null>(null);

    // Store the timestamps of when the OTP codes were sent
    const [emailOtpSentTime, setEmailOtpSentTime] = useState<number | null>(null);
    const [smsOtpSentTime, setSmsOtpSentTime] = useState<number | null>(null);

    // Calculate time left based on the stored timestamp
    const calculateTimeLeft = (sentTime: number | null, duration: number = otpSendInterval) => {
        if (!sentTime) return duration;
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        const elapsed = currentTime - sentTime;
        return Math.max(0, duration - elapsed);
    };

    // useCallback for handleSendEmailCode to prevent unnecessary re-renders
    const handleSendEmailCode = useCallback(async () => {
        if (!selectedMethod) {
            return;
        }

        if (calculateTimeLeft(emailOtpSentTime) > 0 && emailOtpSentTime !== null) {
            return;
        }
        setEmailOtpSentTime(Math.floor(Date.now() / 1000)); // Store the current time in seconds
        await sendOtpEmail(sessionData?.session_id!!);
    }, [emailOtpSentTime, selectedMethod, sendOtpEmail, sessionData]); // Add dependencies that might change

    // useCallback for handleSendSMSCode
    const handleSendSMSCode = useCallback(async () => {
        if (!selectedMethod) {
            return;
        }

        if (calculateTimeLeft(smsOtpSentTime) > 0 && smsOtpSentTime !== null) {
            return;
        }
        setSmsOtpSentTime(Math.floor(Date.now() / 1000)); // Store the current time in seconds
        await sendOtpSms(sessionData?.session_id!!);
    }, [smsOtpSentTime, selectedMethod, sendOtpSms, sessionData]); // Add dependencies that might change


    useEffect(() => {
        if (selectedMethod === AvailableAuthMethods.OTP_EMAIL && emailOtpSentTime === null) {
            handleSendEmailCode()
        } else if (selectedMethod === AvailableAuthMethods.OTP_SMS && smsOtpSentTime === null) {
            handleSendSMSCode()
        }
    }, [selectedMethod, emailOtpSentTime, smsOtpSentTime, handleSendEmailCode, handleSendSMSCode]);

    useEffect(() => {
        if (!sessionData ||
            !sessionData.available_auth_methods ||
            sessionData.available_auth_methods.length === 0) {
            navigate('/login/error', {
                state: {
                    errorMessage: t('error_unexpected'),
                    error: 'internal_error_no_auth_methods',
                }
            });
            return;
        }

        // Filter available methods that are MFA methods
        const methods = sessionData.available_auth_methods.filter(
            method => MFAMethods.includes(method as AvailableAuthMethods)
        ) as AvailableAuthMethods[];

        // Sort methods based on the specific order, any methods not in the list will come after
        methods.sort((a, b) => {
            const indexA = methodOrder.indexOf(a);
            const indexB = methodOrder.indexOf(b);

            // If both methods are in the predefined order, sort them by their index in methodOrder
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }

            // If a is in the order but b is not, a should come first
            if (indexA !== -1) return -1;

            // If b is in the order but a is not, b should come first
            if (indexB !== -1) return 1;

            // If neither are in the order, maintain their original order
            return 0;
        });
        setSelectedMethod(methods[0]);
        setAvailableMethods(methods);
    }, [sessionData, navigate, t]);

    if (!sessionData || !sessionData.available_auth_methods || sessionData.available_auth_methods.length === 0) {
        return null; // Return null while redirecting to avoid rendering the component
    }

    const handleMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setError(null);
        setSelectedMethod(event.target.value as AvailableAuthMethods);
    };

    const handleCancel = () => {
        navigate('/login/error', {
            state: {
                errorMessage: t('error_login_cancelled'),
                error: 'login_cancelled',
            }
        });
    }

    // Handle form submission
    const handleSubmit = async () => {
        if (!code) {
            return;
        }
        setError(null);
        setLoading(true); // Start loading

        try {
            let res: SessionData;
            switch (selectedMethod) {
                case AvailableAuthMethods.TOTP:
                    res = await submitOTP(code, sessionData?.session_id!!);
                    break;
                case AvailableAuthMethods.OTP_EMAIL:
                    res = await submitOtpEmail(code, sessionData?.session_id!!);
                    break;
                case AvailableAuthMethods.OTP_SMS:
                    res = await submitOtpSMS(code, sessionData?.session_id!!);
                    break;
                default:
                    navigate('/login/error', {
                        state: {
                            errorMessage: t('error_unexpected'),
                            error: 'internal_error',
                        }
                    });
                    return;
            }
            setSessionData(res);
            navigate('/login/consent');
        } catch (err) {
            console.error("Failed to submit code:", err);
            if (err === errorInvalidCredentials) {
                setError(t('error_invalid_code'));
                return;
            }
            navigate('/login/error', {
                state: {
                    errorMessage: t('error_unexpected'),
                    error: 'internal_error',
                }
            });
        } finally {
            setLoading(false);
        }
    };

    // 1. Case when there's only one auth method available
    if (availableMethods.length === 1) {
        const method = availableMethods[0];

        // Authenticator App (TOTP)
        if (method === AvailableAuthMethods.TOTP) {
            return (
                <div className="login-container">
                    <div className="logo"></div>
                    <h2 className="login-title">{t('ui_single_auth_topt')}</h2>
                    <UserInfo sessionData={sessionData} />
                    <div className="login-box">
                        <div className="input-container">
                            <label htmlFor="code">{t('ui_code')}</label>
                            <input
                                type="text"
                                id="code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                placeholder={t('ui_enter_code')}
                            />
                            {error && <span className="error-message">{error}</span>}
                        </div>
                        {loading ? (
                            <div className="spinner-container">
                                <div className="spinner"></div> {/* Add spinner styles here */}
                            </div>
                        ) : (
                            <>
                                <button className="submit-button" onClick={handleSubmit} disabled={!code && !selectedMethod}>
                                    {t('ui_submit_code')}
                                </button>
                                <button className="back-button" onClick={handleCancel}>
                                    {t('ui_cancel')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            );
        }

        // Email OTP
        if (method === AvailableAuthMethods.OTP_EMAIL) {
            return (
                <div className="login-container">
                    <div className="logo"></div>
                    <h2 className="login-title">{t('ui_single_auth_email')}</h2>
                    <UserInfo sessionData={sessionData} />
                    <div className="login-box">
                        <div className="input-container">
                            <label htmlFor="code">{t('ui_code')}</label>
                            <input
                                type="text"
                                id="code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                placeholder={t('ui_enter_code')}
                            />
                            {error && <span className="error-message">{error}</span>}
                            <div className="send-code-container">
                                <p className="send-code-text">{t('ui_email_code_sent')}</p>
                                <SendCodeButton
                                    onSendCode={handleSendEmailCode}
                                    initialTime={calculateTimeLeft(emailOtpSentTime)}
                                    startCountdown={emailOtpSentTime !== null} />
                            </div>
                        </div>
                        {loading ? (
                            <div className="spinner-container">
                                <div className="spinner"></div> {/* Add spinner styles here */}
                            </div>
                        ) : (
                            <>
                                <button className="submit-button" onClick={handleSubmit} disabled={!code && !selectedMethod}>
                                    {t('ui_submit_code')}
                                </button>
                                <button className="back-button" onClick={handleCancel}>
                                    {t('ui_cancel')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            );
        }

        // SMS OTP
        if (method === AvailableAuthMethods.OTP_SMS) {
            return (
                <div className="login-container">
                    <div className="logo"></div>
                    <h2 className="login-title">{t('ui_single_auth_sms')}</h2>
                    <UserInfo sessionData={sessionData} />
                    <div className="login-box">
                        <label>{t('ui_sms_code_sent')}</label>
                        <SendCodeButton
                            onSendCode={handleSendSMSCode}
                            initialTime={calculateTimeLeft(smsOtpSentTime)}
                            startCountdown={smsOtpSentTime !== null} />
                        <div className="input-container">
                            <label htmlFor="code">{t('ui_code')}</label>
                            <input
                                type="text"
                                id="code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                placeholder={t('ui_enter_code')}
                            />
                            {error && <span className="error-message">{error}</span>}
                        </div>
                        {loading ? (
                            <div className="spinner-container">
                                <div className="spinner"></div> {/* Add spinner styles here */}
                            </div>
                        ) : (
                            <>
                                <button className="submit-button" onClick={handleSubmit} disabled={!code && !selectedMethod}>
                                    {t('ui_submit_code')}
                                </button>
                                <button className="back-button" onClick={handleCancel}>
                                    {t('ui_cancel')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            );
        }
    }

    // 2. Case when there are multiple auth methods available
    return (
        <div className="login-container">
            <div className="logo"></div>
            <h2 className="login-title">{t('ui_select_auth_method')}</h2>
            <UserInfo sessionData={sessionData} />
            <div className="login-box">
                {availableMethods.length > 1 && (
                    <>
                        {availableMethods.map((method) => (
                            <label key={method} className="mfa-checkbox-label custom-radio-label">
                                <input
                                    type="radio"
                                    name="authMethod"
                                    value={method}
                                    onChange={handleMethodChange}
                                    checked={selectedMethod === method}
                                />
                                <span className="custom-radio"></span> {/* Custom radio button */}
                                {t(method)}
                            </label>
                        ))}
                    </>
                )}
                <div className="input-container">
                    <label htmlFor="code"></label>
                    <input
                        type="text"
                        id="code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder={t('ui_enter_code')}
                    />
                    {error && <span className="error-message">{error}</span>}
                    {/* Email OTP */}
                    {selectedMethod === AvailableAuthMethods.OTP_EMAIL && (
                        <div className="send-code-container">
                            <p className="send-code-text">{t('ui_email_code_sent')}</p>
                            <SendCodeButton
                                onSendCode={handleSendEmailCode}
                                initialTime={calculateTimeLeft(emailOtpSentTime)}
                                startCountdown={emailOtpSentTime !== null}
                            />
                        </div>
                    )}

                    {/* SMS OTP */}
                    {selectedMethod === AvailableAuthMethods.OTP_SMS && (
                        <div className="send-code-container">
                            <p className="send-code-text">{t('ui_sms_code_sent')}</p>
                            <SendCodeButton
                                onSendCode={handleSendSMSCode}
                                initialTime={calculateTimeLeft(smsOtpSentTime)}
                                startCountdown={smsOtpSentTime !== null}
                            />
                        </div>
                    )}
                </div>


                {loading ? (
                    <div className="spinner-container">
                        <div className="spinner"></div> {/* Add spinner styles here */}
                    </div>
                ) : (
                    <>
                        <button className="submit-button" onClick={handleSubmit} disabled={!code && !selectedMethod}>
                            {t('ui_submit_code')}
                        </button>
                        <button className="back-button" onClick={handleCancel}>
                            {t('ui_cancel')}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginMFA;
